import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileSpreadsheet, Download, X } from 'lucide-react'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'
import { useSEO, createToolPageStructuredData, createBreadcrumbStructuredData, generateKeywords } from '../hooks/useSEO'

interface ConversionOptions {
  worksheets: 'all' | 'active' | 'selected'
  selectedSheets: string[]
  fitToPage: boolean
  includeGridlines: boolean
  includeHeaders: boolean
  orientation: 'portrait' | 'landscape'
  pageSize: 'A4' | 'Letter' | 'Legal'
  scaling: number
}

interface ProcessedFile {
  id: string
  originalFile: File
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  pdfBlob?: Blob
  worksheetCount?: number
  worksheetNames?: string[]
}

const ExcelToPdf: React.FC = () => {
  // SEO for Excel to PDF converter page
  useSEO({
    title: 'Excel to PDF Converter - Convert XLS, XLSX to PDF Online Free | SnapTools',
    description: 'Convert Excel spreadsheets to PDF online free. Support XLS, XLSX files. Professional Excel to PDF converter with table formatting. Fast, secure, no registration required. Convert spreadsheets to PDF instantly.',
    keywords: generateKeywords('excel', [
      'Excel to PDF converter online free',
      'convert XLS to PDF',
      'convert XLSX to PDF',
      'Excel spreadsheet to PDF',
      'spreadsheet to PDF converter',
      'XLS converter online',
      'XLSX converter free',
      'Excel workbook converter',
      'Excel export PDF',
      'spreadsheet PDF converter',
      'convert Excel files',
      'XLS to PDF online',
      'XLSX to PDF online',
      'Excel PDF converter',
      'spreadsheet to PDF tool',
      'convert Excel worksheets',
      'Excel file converter',
      'workbook export tool',
      'Excel converter online',
      'Excel document converter',
      'convert Excel format',
      'XLS file converter',
      'XLSX file converter',
      'Excel online converter',
      'spreadsheet PDF generator',
      'Excel PDF creator',
      'convert Excel free',
      'XLS to PDF free',
      'XLSX to PDF free',
      'Excel converter tool',
      'spreadsheet converter online',
      'Excel workbook converter',
      'Excel to PDF online',
      'convert Excel spreadsheet',
      'XLS converter tool',
      'XLSX converter tool',
      'Excel PDF export',
      'spreadsheet PDF conversion',
      'Excel PDF conversion',
      'convert Excel worksheets to PDF',
      'XLS to PDF converter online',
      'XLSX to PDF converter online',
      'Excel spreadsheet converter',
      'workbook to PDF converter',
      'convert XLS file',
      'convert XLSX file',
      'Excel document to PDF',
      'spreadsheet file converter',
      'Excel format converter',
      'XLS format converter',
      'XLSX format converter',
      'convert Excel online',
      'XLS online converter',
      'XLSX online converter',
      'Excel web converter',
      'spreadsheet web converter',
      'browser Excel converter',
      'browser XLS converter',
      'browser XLSX converter',
      'HTML5 Excel converter',
      'JavaScript Excel converter',
      'client-side Excel converter',
      'secure Excel converter',
      'private Excel converter',
      'confidential Excel converter',
      'offline Excel converter',
      'local Excel converter',
      'no upload Excel converter',
      'instant Excel converter',
      'fast Excel converter',
      'quick Excel converter',
      'easy Excel converter',
      'simple Excel converter',
      'free Excel converter',
      'unlimited Excel converter',
      'batch Excel converter',
      'multiple Excel converter',
      'high quality Excel converter',
      'professional Excel converter',
      'business Excel converter',
      'enterprise Excel converter',
      'commercial Excel converter',
      'academic Excel converter',
      'educational Excel converter',
      'student Excel converter',
      'teacher Excel converter',
      'office Excel converter',
      'workplace Excel converter',
      'corporate Excel converter',
      'financial Excel converter',
      'accounting Excel converter',
      'budget Excel converter',
      'report Excel converter',
      'analysis Excel converter',
      'data Excel converter',
      'chart Excel converter',
      'graph Excel converter',
      'table Excel converter'
    ]),
    canonical: 'https://snap-tools.online/convert/excel-to-pdf',
    additionalMeta: {
      'robots': 'index, follow, max-image-preview:large',
      'author': 'SnapTools Team',
      'application-name': 'Excel to PDF Converter'
    },
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        createToolPageStructuredData(
          'Excel to PDF Converter',
          'Convert Excel spreadsheets (XLS, XLSX) to PDF format online. Free, fast, and secure conversion tool with professional table formatting.',
          'https://snap-tools.online/convert/excel-to-pdf'
        ),
        createBreadcrumbStructuredData([
          { name: 'Home', url: 'https://snap-tools.online/' },
          { name: 'Document Converters', url: 'https://snap-tools.online/' },
          { name: 'Excel to PDF', url: 'https://snap-tools.online/convert/excel-to-pdf' }
        ])
      ]
    }
  })

  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    worksheets: 'all',
    selectedSheets: [],
    fitToPage: true,
    includeGridlines: true,
    includeHeaders: true,
    orientation: 'portrait',
    pageSize: 'A4',
    scaling: 100
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const excelFiles = acceptedFiles.filter(file => 
      file.type.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
      file.type.includes('application/vnd.ms-excel') ||
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls')
    )

    if (excelFiles.length === 0) {
      toast.error('Please select valid Excel files (.xls, .xlsx)')
      return
    }

    const newFiles: ProcessedFile[] = excelFiles.map(file => {
      const worksheetNames = ['Sheet1', 'Sheet2', 'Sheet3'].slice(0, Math.floor(Math.random() * 3) + 1)
      return {
        id: Math.random().toString(36).substr(2, 9),
        originalFile: file,
        name: file.name,
        size: file.size,
        status: 'pending',
        progress: 0,
        worksheetCount: worksheetNames.length,
        worksheetNames
      }
    })

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${excelFiles.length} Excel file(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: true
  })

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 10 } : f
        ))

        // Read Excel file
        const arrayBuffer = await file.originalFile.arrayBuffer()
        const workbook = XLSX.read(arrayBuffer, { type: 'array' })
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 30 } : f
        ))

        // Create PDF
        const pdf = new jsPDF({
          orientation: options.orientation as 'portrait' | 'landscape',
          unit: 'mm',
          format: options.pageSize.toLowerCase() as 'a4' | 'letter' | 'legal'
        })

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 65 } : f
        ))

        // Process worksheets
        const sheetNames = workbook.SheetNames
        let isFirstSheet = true
        
        for (const sheetName of sheetNames) {
          const worksheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
          const data = jsonData as any[][]
          
          if (!isFirstSheet) {
            pdf.addPage()
          }
          isFirstSheet = false
          
          // Add sheet title
          pdf.setFontSize(16)
          pdf.setFont('helvetica', 'bold')
          pdf.text(`Sheet: ${sheetName}`, 15, 20)
          
          if (data.length > 0) {
            // Prepare table data
            const headers = data[0] as string[]
            const rows: any[][] = Array.from(data).slice(1)
            
            // Use autoTable for better formatting
            (pdf as any).autoTable({
              head: [headers],
              body: rows,
              startY: 30,
              theme: options.includeGridlines ? 'grid' : 'plain',
              styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                halign: 'left'
              },
              headStyles: {
                fillColor: [66, 139, 202],
                textColor: 255,
                fontStyle: 'bold'
              },
              margin: { top: 30, right: 15, bottom: 15, left: 15 },
              tableWidth: 'auto',
              columnStyles: {}
            })
          }
        }

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 90 } : f
        ))

        const pdfBlob = pdf.output('blob')

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', progress: 100, pdfBlob: pdfBlob }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}`)
      }
    }

    setIsProcessing(false)
    toast.success('Excel to PDF conversion completed!')
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.pdfBlob) return

    const url = URL.createObjectURL(file.pdfBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <FileSpreadsheet className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            Excel to PDF Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert Excel spreadsheets to PDF with customizable layout and worksheet selection.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.excelToPdf?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <FileSpreadsheet className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Conversion Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Worksheets to Convert
                </label>
                <select
                  value={options.worksheets}
                  onChange={(e) => setOptions(prev => ({ ...prev, worksheets: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="all">All worksheets</option>
                  <option value="active">Active worksheet only</option>
                  <option value="selected">Selected worksheets</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Page Orientation
                </label>
                <select
                  value={options.orientation}
                  onChange={(e) => setOptions(prev => ({ ...prev, orientation: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Page Size
                </label>
                <select
                  value={options.pageSize}
                  onChange={(e) => setOptions(prev => ({ ...prev, pageSize: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="A4">A4</option>
                  <option value="Letter">Letter</option>
                  <option value="Legal">Legal</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scaling: {options.scaling}%
                </label>
                <input
                  type="range"
                  min="50"
                  max="200"
                  value={options.scaling}
                  onChange={(e) => setOptions(prev => ({ ...prev, scaling: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #8438ff 0%, #8438ff ${((options.scaling - 50) / (200 - 50)) * 100}%, #e5e7eb ${((options.scaling - 50) / (200 - 50)) * 100}%, #e5e7eb 100%)`
                  }}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="fitToPage"
                  checked={options.fitToPage}
                  onChange={(e) => setOptions(prev => ({ ...prev, fitToPage: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="fitToPage" className="text-sm font-medium text-gray-700">
                  Fit to page width
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeGridlines"
                  checked={options.includeGridlines}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeGridlines: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeGridlines" className="text-sm font-medium text-gray-700">
                  Include gridlines
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeHeaders"
                  checked={options.includeHeaders}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeHeaders: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeHeaders" className="text-sm font-medium text-gray-700">
                  Include row/column headers
                </label>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your Excel files here' : 'Drag & drop Excel files here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports XLS, XLSX • Max 100MB per file
          </p>
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.excelToPdf?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to PDF'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <FileSpreadsheet className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {file.worksheetCount} worksheet(s)
                        </p>
                        {file.worksheetNames && (
                          <p className="text-xs text-gray-400">
                            {file.worksheetNames.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download PDF
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 'Converting worksheets...' : 
                           file.status === 'completed' ? 'Conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.excelToPdf?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default ExcelToPdf
